<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import type { ChatMentionCandidate } from '@/modules/chat-matrix-fast/utils/mentions'
import { getAvatarColor, getInitials } from '../../chatModules'
import AuthenticatedVImg from '../AuthenticatedVImg.vue'

const props = defineProps<{
  items: ChatMentionCandidate[]
  highlightedIndex: number
  panelStyle?: Record<string, string>
}>()

const emit = defineEmits<{
  (e: 'select', item: ChatMentionCandidate): void
  (e: 'highlight', index: number): void
}>()

const listRef = ref<HTMLElement | null>(null)

const scrollHighlightedIntoView = () => {
  nextTick(() => {
    const list = listRef.value
    if (!list) return
    const active = list.querySelector('.mention-picker__item--active') as HTMLElement | null
    active?.scrollIntoView({ block: 'nearest' })
  })
}

watch(() => props.highlightedIndex, scrollHighlightedIntoView)
watch(() => props.items.length, scrollHighlightedIntoView)
</script>

<template>
  <div
    ref="listRef"
    class="mention-picker"
    role="listbox"
    :style="panelStyle"
  >
    <div
      v-if="!items.length"
      class="mention-picker__empty"
    >
      Нет участников для упоминания
    </div>
    <button
      v-for="(item, index) in items"
      :key="item.key"
      type="button"
      class="mention-picker__item"
      :class="{ 'mention-picker__item--active': index === highlightedIndex }"
      role="option"
      :aria-selected="index === highlightedIndex"
      @mouseenter="emit('highlight', index)"
      @mousedown.prevent="emit('select', item)"
    >
      <VAvatar
        size="28"
        :class="[!item.avatar ? getAvatarColor(item.displayName) : '']"
      >
        <AuthenticatedVImg
          v-if="item.avatar"
          :src="item.avatar"
        />
        <span
          v-else
          class="text-caption"
        >{{ getInitials(item.displayName) }}</span>
      </VAvatar>
      <span class="mention-picker__name text-truncate">{{ item.displayName }}</span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.mention-picker {
  max-height: 220px;
  overflow-y: auto;
  border-radius: 10px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-outline), 0.16);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.mention-picker__empty {
  padding: 12px;
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.mention-picker__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:hover,
  &--active {
    background: rgba(var(--v-theme-primary), 0.1);
  }
}

.mention-picker__name {
  font-size: 14px;
  font-weight: 500;
  min-width: 0;
}
</style>
